import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save, Search } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/Button';

/** `/settings/labels` (v0.4 章③、D-29/D-67/D-70)。
 *  マスタ画面の項目名 (label_key_catalog) をテナント別に上書きする (tenant_labels)。
 *  設計: docs/design/v04-shift-labor-management-detail.md §3-5
 *  GET /labels/catalog 自体が会社管理者以上限定のため、このページに到達し取得に成功した時点で
 *  編集権限はある (feature flag OFF のケースのみ PUT/DELETE 時に FEATURE_DISABLED で弾かれる)。
 *
 *  v0.4 F-1 (D-197②): is_enabled トグルで「上書き文言は残したまま一時的に無効化」できるようにする
 *  (`docs/design/v04-handoff-next-session.md` §0-J-6)。 */

type LabelCatalogRow = {
  label_key: string;
  default_label: string;
  category: 'entity' | 'field' | 'nav' | 'attr' | 'usage' | 'set' | 'enum';
  entity: string | null;
  field: string | null;
  description: string | null;
  current_text: string | null;
  is_enabled: boolean;
};

function LabelRow({ row, onSaved }: { row: LabelCatalogRow; onSaved: () => void }) {
  const persistedValue = row.current_text ?? row.default_label;
  const [text, setText] = useState(persistedValue);
  const hasOverride = row.current_text !== null;
  const trimmed = text.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= 100 && trimmed !== persistedValue;

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/labels/${encodeURIComponent(row.label_key)}`, {
      method: 'PUT', body: JSON.stringify({ label_text: trimmed }),
    }),
    onSuccess: () => { onSaved(); toast.success('用語を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });
  const resetMut = useMutation({
    mutationFn: () => apiFetch(`/labels/${encodeURIComponent(row.label_key)}`, { method: 'DELETE' }),
    onSuccess: () => { onSaved(); toast.success('既定の用語に戻しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '操作に失敗しました'),
  });
  const toggleEnabledMut = useMutation({
    mutationFn: (nextEnabled: boolean) => apiFetch(`/labels/${encodeURIComponent(row.label_key)}/enabled`, {
      method: 'PATCH', body: JSON.stringify({ is_enabled: nextEnabled }),
    }),
    onSuccess: (_data, nextEnabled) => {
      onSaved();
      toast.success(nextEnabled ? '上書きを再度有効にしました' : '上書きを一時的に無効化しました (既定の用語で表示されます)');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '操作に失敗しました'),
  });

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 px-3 sm:px-4">
      <div className="sm:w-56 shrink-0">
        <div className="text-sm text-text-body">{row.default_label}</div>
        {row.description && <div className="text-xs text-text-sub mt-0.5">{row.description}</div>}
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-2 min-w-0">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={100}
          aria-label={`${row.default_label}の表示名`}
          disabled={hasOverride && !row.is_enabled}
          className="flex-1 min-w-0 min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring disabled:opacity-45 disabled:bg-surface-sunken"
        />
        <Button variant="secondary" size="sm" leftIcon={<Save size={14} />}
          disabled={!canSave || saveMut.isPending || (hasOverride && !row.is_enabled)} loading={saveMut.isPending}
          onClick={() => saveMut.mutate()}>
          保存
        </Button>
        {hasOverride && (
          <>
            <button
              type="button"
              aria-pressed={row.is_enabled}
              aria-label={row.is_enabled
                ? `${row.default_label}の上書きを一時的に無効化する (既定の用語「${row.default_label}」に戻す)`
                : `${row.default_label}の上書きを再度有効にする`}
              title={row.is_enabled ? '一時的に無効化する' : '再度有効にする'}
              onClick={() => toggleEnabledMut.mutate(!row.is_enabled)}
              disabled={toggleEnabledMut.isPending}
              className={`min-h-[44px] px-2.5 rounded text-xs border transition disabled:opacity-45 ${
                row.is_enabled
                  ? 'border-surface-border text-text-sub hover:bg-surface-sunken'
                  : 'border-misespark-navy/30 bg-misespark-navy/10 text-misespark-navy'
              }`}
            >
              {row.is_enabled ? '有効' : '無効化中'}
            </button>
            <button
              type="button"
              aria-label={`${row.default_label}の上書きを削除し既定の用語「${row.default_label}」に戻す`}
              title="上書きを削除する"
              onClick={() => resetMut.mutate()}
              disabled={resetMut.isPending}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-sub hover:text-text-body disabled:opacity-40"
            >
              <RotateCcw size={16} aria-hidden />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** 使い勝手フィードバック (2026-09-04、③): 用語が多すぎて辿り着けないという指摘への対応。
 *  完全一致ではなく部分一致 (大文字小文字を無視) で、既定名・現在の表示名・説明文のいずれかに
 *  ヒットすれば絞り込む。「カスタム済み」は current_text !== null (label_key_catalog にテナント側の
 *  上書き行が存在するか) で判定する。 */
type CustomFilter = 'all' | 'default' | 'custom';

const matchesQuery = (row: LabelCatalogRow, query: string): boolean => {
  if (!query) return true;
  const haystack = [row.default_label, row.current_text ?? '', row.description ?? ''].join(' ').toLowerCase();
  return haystack.includes(query);
};

export function LabelsSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery<LabelCatalogRow[]>({
    queryKey: ['labels-catalog'],
    queryFn: () => apiFetch<LabelCatalogRow[]>('/labels/catalog'),
  });
  const [query, setQuery] = useState('');
  const [customFilter, setCustomFilter] = useState<CustomFilter>('all');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['labels-catalog'] });
    qc.invalidateQueries({ queryKey: ['labels'] });
  };

  const allRows = data ?? [];
  const normalizedQuery = query.trim().toLowerCase();
  const rows = useMemo(() => allRows.filter((r) => {
    if (customFilter === 'custom' && r.current_text === null) return false;
    if (customFilter === 'default' && r.current_text !== null) return false;
    return matchesQuery(r, normalizedQuery);
  }), [allRows, normalizedQuery, customFilter]);
  const isFiltering = normalizedQuery.length > 0 || customFilter !== 'all';

  // v0.4 F-1 (nav.shift 等): ナビゲーショングループ名。entity/field と別セクションで表示する
  const navRows = useMemo(() => rows.filter((r) => r.category === 'nav'), [rows]);
  const entityRows = useMemo(() => rows.filter((r) => r.category === 'entity'), [rows]);
  // field 行を entity ごとにグループ化 (カタログの並び順を保ったまま初出順でグループを作る)
  const fieldGroups = useMemo(() => {
    const map = new Map<string, LabelCatalogRow[]>();
    for (const r of rows) {
      if (r.category !== 'field') continue;
      const key = r.entity ?? '';
      const list = map.get(key);
      if (list) list.push(r); else map.set(key, [r]);
    }
    return map;
  }, [rows]);
  // グループ見出しの表示名 (entityDisplayName) は絞り込みの影響を受けず常に全件から解決する
  // (該当エンティティ名の行自体が絞り込みで隠れても、field グループの見出しは表示し続けたいため)。
  // グループ見出しは可能な限り「エンティティ名」セクションの現在の表示名を使う
  const entityDisplayName = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of allRows) {
      if (r.category !== 'entity') continue;
      const key = r.entity ?? r.label_key.replace(/^entity\./, '');
      map.set(key, r.current_text ?? r.default_label);
    }
    return map;
  }, [allRows]);

  if (isError) {
    if (error instanceof ApiClientError && error.status === 403) {
      return (
        <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">
          権限がありません。
        </div>
      );
    }
    return (
      <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">
        用語設定の取得に失敗しました。
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">用語のカスタマイズ</h2>
        <p className="text-sm text-text-sub mt-1">マスタ画面の項目名をテナント独自の用語に変更できます。</p>
      </div>

      {!isLoading && allRows.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1 min-w-0 sm:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="用語を検索 (部分一致)"
              aria-label="用語を検索"
              className="w-full min-h-[44px] pl-9 pr-3 py-2 rounded border border-surface-border focus-ring"
            />
          </div>
          <div className="flex rounded-full border border-surface-border overflow-hidden shrink-0" role="group" aria-label="絞り込み">
            {([
              { value: 'all', label: 'すべて' },
              { value: 'custom', label: '変更済み' },
              { value: 'default', label: 'デフォルト' },
            ] as const).map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={customFilter === o.value}
                onClick={() => setCustomFilter(o.value)}
                className={`px-3 min-h-[44px] text-xs font-medium ${customFilter === o.value ? 'bg-brand-red text-white' : 'bg-white text-text-body hover:bg-surface-sunken'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {isFiltering && (
        <p className="text-xs text-text-sub" aria-live="polite">{rows.length}件 / 全{allRows.length}件</p>
      )}

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : allRows.length === 0 ? (
        <EmptyState message="カスタマイズ可能な用語がありません" />
      ) : rows.length === 0 ? (
        <EmptyState message="該当する用語がありません" description="検索語や絞り込み条件を変更してください。" />
      ) : (
        <div className="space-y-6">
          {navRows.length > 0 && (
            <section className="border border-surface-border rounded-lg bg-white">
              <h3 className="px-3 sm:px-4 py-2 text-sm font-semibold text-text-body border-b border-surface-border bg-surface-sunken truncate">
                ナビゲーション
              </h3>
              <div className="divide-y divide-surface-border">
                {navRows.map((row) => (
                  <LabelRow key={`${row.label_key}:${row.current_text ?? ''}`} row={row} onSaved={invalidate} />
                ))}
              </div>
            </section>
          )}

          {entityRows.length > 0 && (
            <section className="border border-surface-border rounded-lg bg-white">
              <h3 className="px-3 sm:px-4 py-2 text-sm font-semibold text-text-body border-b border-surface-border bg-surface-sunken truncate">
                エンティティ名
              </h3>
              <div className="divide-y divide-surface-border">
                {entityRows.map((row) => (
                  <LabelRow key={`${row.label_key}:${row.current_text ?? ''}`} row={row} onSaved={invalidate} />
                ))}
              </div>
            </section>
          )}

          {[...fieldGroups.entries()].map(([entityKey, list]) => (
            <section key={entityKey || '(unknown)'} className="border border-surface-border rounded-lg bg-white">
              <h3 className="px-3 sm:px-4 py-2 text-sm font-semibold text-text-body border-b border-surface-border bg-surface-sunken truncate">
                {entityDisplayName.get(entityKey) ?? entityKey}
              </h3>
              <div className="divide-y divide-surface-border">
                {list.map((row) => (
                  <LabelRow key={`${row.label_key}:${row.current_text ?? ''}`} row={row} onSaved={invalidate} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
